import { useState } from 'react'
import {
  useProduct,
  useProductDocuments,
  useDownloadContent,
  useRemoveProductDocument,
} from '@/hooks/use-api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatDate, formatCurrency } from '@/lib/utils'
import { Package, AlertCircle, Pencil, Download, Trash2, Upload } from 'lucide-react'
import { EditProductModal } from '@/components/EditProductModal'
import { UploadContentModal } from '@/components/UploadContentModal'
import { RepositoryAssetThumbnail } from '@/components/RepositoryAssetThumbnail'
import { useToast } from '@/hooks/use-toast'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { explainError } from '@/lib/explain-error'

interface ProductDetailModalProps {
  productId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  canEdit?: boolean
}

export function ProductDetailModal({
  productId,
  open,
  onOpenChange,
  canEdit = false,
}: ProductDetailModalProps) {
  const { data: product, isLoading, error } = useProduct(productId || '')
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [uploadDocOpen, setUploadDocOpen] = useState(false)

  // A product has a price per currency, never one price. Empty means it cannot
  // be quoted at all — distinct from "no price in the currency you asked for".
  const prices = product?.prices ?? []

  const { data: docsResponse } = useProductDocuments(productId || undefined)
  const documents = docsResponse?.data || []
  const ASSET_TYPES = usePicklistOptions('content', 'asset_type')
  const downloadDoc = useDownloadContent()
  const removeDoc = useRemoveProductDocument()
  const { toast } = useToast()

  const handleDownloadDoc = async (id: string, fileName: string) => {
    try {
      const blob = await downloadDoc.mutateAsync(id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      toast({
        title: 'Download failed',
        description: explainError(err, 'Could not download'),
        variant: 'destructive',
      })
    }
  }

  const handleRemoveDoc = async (linkId: string, title: string) => {
    if (!productId) return
    if (!confirm(`Unlink "${title}" from this product?`)) return
    try {
      await removeDoc.mutateAsync({ productId, linkId })
      toast({ title: 'Unlinked', description: title })
    } catch (err) {
      toast({
        title: 'Could not unlink',
        description: explainError(err, 'Unknown error'),
        variant: 'destructive',
      })
    }
  }

  if (!open) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] overflow-y-auto"
        aria-describedby="product-description"
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" aria-hidden="true" />
              {product?.name || 'Product Details'}
            </DialogTitle>
            {canEdit && product && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditModalOpen(true)}
                aria-label="Edit product"
              >
                <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
                Edit
              </Button>
            )}
          </div>
          <DialogDescription id="product-description">
            View product information and details
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading product details...</div>
        ) : error ? (
          <div className="text-center py-8 text-destructive">Failed to load product details</div>
        ) : product ? (
          <div className="space-y-6">
            {/* Basic Information */}
            <section>
              <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                Basic Information
              </h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Product Name</dt>
                  <dd className="text-sm mt-1">{product.name}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                  <dd className="text-sm mt-1 flex gap-2">
                    <Badge variant={product.is_active ? 'success' : 'destructive'}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {product.renewal && <Badge variant="secondary">Renewal</Badge>}
                  </dd>
                </div>

                {product.product_code && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Product Code</dt>
                    <dd className="text-sm mt-1">{product.product_code}</dd>
                  </div>
                )}

                {product.family && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Family</dt>
                    <dd className="text-sm mt-1">{product.family}</dd>
                  </div>
                )}

                {product.currency_iso_code && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Currency</dt>
                    <dd className="text-sm mt-1">{product.currency_iso_code}</dd>
                  </div>
                )}

                {product.stock_keeping_unit && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">SKU</dt>
                    <dd className="text-sm mt-1">{product.stock_keeping_unit}</dd>
                  </div>
                )}

                {product.quantity_unit_of_measure && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Unit of Measure</dt>
                    <dd className="text-sm mt-1">{product.quantity_unit_of_measure}</dd>
                  </div>
                )}
              </dl>
            </section>

            {/* Prices — one row per currency.
                This section used to show a single "Unit Price", resolved in
                whichever currency the caller asked for. The catalogue asks for
                none, so it fell back to CAD and would report "no pricing" for
                62 of 70 products that are priced perfectly well in USD. A
                product does not have a price; it has a price per currency. */}
            <section>
              <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">Prices</h3>
              {prices.length > 0 ? (
                <>
                  <table className="w-full text-sm" aria-label="Prices by currency">
                    <caption className="sr-only">
                      List price for {product.name} in each currency
                    </caption>
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th scope="col" className="text-left font-medium py-1">
                          Currency
                        </th>
                        <th scope="col" className="text-right font-medium py-1">
                          List price
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {prices.map((price) => (
                        <tr key={price.pricebook_entry_id} className="border-b last:border-0">
                          <th scope="row" className="text-left font-normal py-1">
                            {price.currency_iso_code}
                            {!price.is_governed && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                not a selling currency
                              </span>
                            )}
                          </th>
                          <td className="text-right py-1 tabular-nums">
                            {formatCurrency(price.unit_price, price.currency_iso_code)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {prices.some((p) => !p.is_governed) && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Prices in currencies GRM does not sell in are shown for reference. They are
                      never offered on a quote.
                    </p>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <AlertCircle className="h-5 w-5 text-warning" aria-hidden="true" />
                  <p className="text-sm">
                    This product has no price in any currency. It cannot be quoted until one is set
                    in the price list.
                  </p>
                </div>
              )}
            </section>

            {/* Description */}
            {product.description && (
              <section>
                <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                  Description
                </h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {product.description}
                </p>
              </section>
            )}

            {/* Documents */}
            <section aria-labelledby="product-documents-heading">
              <div className="flex items-center justify-between mb-3 bg-muted px-3 py-1.5 rounded-sm">
                <h3 id="product-documents-heading" className="text-lg font-semibold">
                  Documents
                </h3>
                {productId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setUploadDocOpen(true)}
                    aria-label="Upload a new document for this product"
                  >
                    <Upload className="h-4 w-4 mr-2" aria-hidden="true" />
                    Upload
                  </Button>
                )}
              </div>
              {documents.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No documents linked to this product. Upload one or promote an existing file from
                  the Repository.
                </p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {documents.map((doc) => (
                    <li
                      key={doc.link_id}
                      className="flex items-center justify-between gap-3 px-3 py-2"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <RepositoryAssetThumbnail
                          contentVersionId={doc.content_version_id}
                          fileType={doc.file_type}
                          hasThumbnail={!!doc.thumbnail_url}
                          title={doc.title}
                          size="row"
                          className="shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{doc.title}</span>
                            <Badge variant="outline" className="shrink-0">
                              {doc.role}
                            </Badge>
                          </div>
                          {doc.repository_summary && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                              {doc.repository_summary}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleDownloadDoc(
                              doc.content_version_id,
                              doc.path_on_client || doc.title
                            )
                          }
                          aria-label={`Download ${doc.title}`}
                        >
                          <Download className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveDoc(doc.link_id, doc.title)}
                            aria-label={`Unlink ${doc.title}`}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* System Information */}
            <section>
              <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                System Information
              </h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Product ID</dt>
                  <dd className="text-sm mt-1 font-mono text-xs">{product.id}</dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Created Date</dt>
                  <dd className="text-sm mt-1">
                    {product.created_at ? formatDate(product.created_at) : '-'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Last Modified</dt>
                  <dd className="text-sm mt-1">
                    {product.updated_at ? formatDate(product.updated_at) : '-'}
                  </dd>
                </div>
              </dl>
            </section>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">Product not found</div>
        )}
      </DialogContent>

      {canEdit && (
        <EditProductModal
          productId={productId}
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
        />
      )}

      {productId && (
        <UploadContentModal
          open={uploadDocOpen}
          onClose={() => setUploadDocOpen(false)}
          repositoryDefaults={{
            section: 'Product',
            asset_type: ASSET_TYPES[0] || 'Other',
            productBinding: { product_id: productId, role: ASSET_TYPES[0] || 'Brochure' },
          }}
        />
      )}
    </Dialog>
  )
}
